import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { todayISO } from "../lib/format";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Bell, FolderKanban, CalendarDays, CalendarClock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ESTADOS = [
  { id: "pendiente", label: "Pendiente" },
  { id: "lista", label: "Lista" },
  { id: "completada", label: "Completada" },
  { id: "cancelada", label: "Cancelada" },
  { id: "anulada", label: "Anulada" },
];

const COLORES = ["#1A3328", "#B8975F", "#2D4A3E", "#A94438", "#C9AE7A", "#52796F", "#7A5028"];

const RECORDATORIOS = [
  { v: 0, label: "A la hora" },
  { v: 5, label: "5 minutos antes" },
  { v: 15, label: "15 minutos antes" },
  { v: 30, label: "30 minutos antes" },
  { v: 60, label: "1 hora antes" },
  { v: 120, label: "2 horas antes" },
  { v: 1440, label: "1 día antes" },
];

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function ymd(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthRange(year, month) {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return { desde: ymd(start), hasta: ymd(end) };
}

function estadoClass(estado) {
  return {
    pendiente: "bg-muted text-foreground",
    lista: "bg-primary/15 text-primary",
    completada: "bg-success/15 text-success",
    cancelada: "bg-warning/20 text-warning-foreground",
    anulada: "bg-destructive/10 text-destructive line-through",
  }[estado] || "bg-muted";
}

const EMPTY_PROYECTO = { nombre: "", descripcion: "", color: COLORES[0] };
const EMPTY_TAREA = {
  proyecto_id: "",
  titulo: "",
  notas: "",
  fecha: todayISO(),
  hora: "09:00",
  recordatorio_minutos: 15,
  estado: "pendiente",
};

export default function Organizador() {
  const { canWrite } = usePermissions();
  const writable = canWrite("organizador");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(todayISO());
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [filtroProyecto, setFiltroProyecto] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [loading, setLoading] = useState(true);

  const [openProyecto, setOpenProyecto] = useState(false);
  const [editingProyecto, setEditingProyecto] = useState(null);
  const [formProyecto, setFormProyecto] = useState(EMPTY_PROYECTO);

  const [openTarea, setOpenTarea] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [formTarea, setFormTarea] = useState(EMPTY_TAREA);
  const [openReagendar, setOpenReagendar] = useState(false);
  const [tareaReagendar, setTareaReagendar] = useState(null);
  const [formReagendar, setFormReagendar] = useState({ fecha: todayISO(), hora: "09:00" });

  const loadProyectos = async () => {
    const { data } = await api.get("/organizador/proyectos");
    setProyectos(data || []);
  };

  const loadTareas = useCallback(async () => {
    const { desde, hasta } = monthRange(year, month);
    const params = { desde, hasta };
    if (filtroProyecto) params.proyecto_id = filtroProyecto;
    if (filtroEstado) params.estado = filtroEstado;
    const { data } = await api.get("/organizador/tareas", { params });
    setTareas(data || []);
  }, [year, month, filtroProyecto, filtroEstado]);

  const load = async () => {
    setLoading(true);
    try {
      await loadProyectos();
      await loadTareas();
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 404
        ? "El servidor aún no tiene el módulo Calendario. Sube el server.py nuevo y reinicia uvicorn."
        : extractError(e);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month, filtroProyecto, filtroEstado]); // eslint-disable-line

  const proyectoMap = useMemo(() => Object.fromEntries(proyectos.map((p) => [p.id, p])), [proyectos]);
  const byDay = useMemo(() => {
    const m = {};
    tareas.forEach((t) => {
      if (!m[t.fecha]) m[t.fecha] = [];
      m[t.fecha].push(t);
    });
    return m;
  }, [tareas]);

  const dayTasks = (byDay[selectedDay] || []).slice().sort((a, b) => (a.hora || "").localeCompare(b.hora || ""));

  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const prevMonth = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); }
    else setMonth(month + 1);
  };

  const saveProyecto = async (e) => {
    e.preventDefault();
    try {
      if (editingProyecto) {
        await api.put(`/organizador/proyectos/${editingProyecto.id}`, formProyecto);
        toast.success("Proyecto actualizado");
      } else {
        await api.post("/organizador/proyectos", formProyecto);
        toast.success("Proyecto creado");
      }
      setOpenProyecto(false);
      setEditingProyecto(null);
      setFormProyecto(EMPTY_PROYECTO);
      loadProyectos();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const saveTarea = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formTarea,
        recordatorio_minutos: Number(formTarea.recordatorio_minutos),
      };
      if (editingTarea) {
        await api.patch(`/organizador/tareas/${editingTarea.id}`, payload);
        toast.success("Tarea actualizada");
      } else {
        await api.post("/organizador/tareas", payload);
        toast.success("Tarea creada");
      }
      setOpenTarea(false);
      setEditingTarea(null);
      loadTareas();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const irAFecha = (fecha) => {
    const [y, m] = fecha.split("-").map(Number);
    setYear(y);
    setMonth(m - 1);
    setSelectedDay(fecha);
  };

  const setEstado = async (t, estado) => {
    if (estado === "reagendada") {
      abrirReagendar(t);
      return;
    }
    try {
      await api.patch(`/organizador/tareas/${t.id}`, { estado });
      loadTareas();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const abrirReagendar = (t) => {
    setTareaReagendar(t);
    setFormReagendar({ fecha: t.fecha, hora: t.hora || "09:00" });
    setOpenReagendar(true);
  };

  const confirmarReagendar = async (e) => {
    e.preventDefault();
    if (!tareaReagendar) return;
    try {
      await api.post(`/organizador/tareas/${tareaReagendar.id}/reagendar`, formReagendar);
      toast.success(`Tarea movida al ${formReagendar.fecha.split("-").reverse().join("/")}`);
      setOpenReagendar(false);
      setTareaReagendar(null);
      irAFecha(formReagendar.fecha);
      await loadTareas();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const eliminarTarea = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea «${t.titulo}»? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/organizador/tareas/${t.id}`);
      toast.success("Tarea eliminada");
      loadTareas();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const eliminarProyecto = async (p) => {
    const msg = p.tareas_abiertas
      ? `«${p.nombre}» tiene ${p.tareas_abiertas} tarea(s) abierta(s). ¿Eliminar el proyecto y TODAS sus tareas?`
      : `¿Eliminar el proyecto «${p.nombre}»?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/organizador/proyectos/${p.id}`, { params: { forzar: true } });
      toast.success("Proyecto eliminado");
      setOpenProyecto(false);
      setEditingProyecto(null);
      if (filtroProyecto === p.id) setFiltroProyecto("");
      await loadProyectos();
      await loadTareas();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const openNewTarea = (fecha) => {
    setEditingTarea(null);
    setFormTarea({
      ...EMPTY_TAREA,
      fecha: fecha || selectedDay,
      proyecto_id: filtroProyecto || proyectos[0]?.id || "",
    });
    setOpenTarea(true);
  };

  return (
    <Page>
      <PageHeader
        title="Calendario y organizador"
        subtitle="Proyectos, tareas por día y hora, recordatorios y seguimiento de estados"
        testid="org-header"
        actions={writable && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setEditingProyecto(null); setFormProyecto(EMPTY_PROYECTO); setOpenProyecto(true); }}
              className="inline-flex items-center gap-1.5 border border-border hover:bg-muted text-sm px-3 py-2 rounded-sm"
            >
              <FolderKanban className="h-4 w-4" /> Nuevo proyecto
            </button>
            <button
              type="button"
              onClick={() => openNewTarea(selectedDay)}
              disabled={proyectos.length === 0}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm disabled:opacity-50"
              data-testid="org-new-task"
            >
              <Plus className="h-4 w-4" /> Nueva tarea
            </button>
          </div>
        )}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr_320px] gap-4">
        <aside className="bg-card border border-border rounded-md p-3">
          <div className="label-caps mb-2">Proyectos</div>
          <button
            type="button"
            onClick={() => setFiltroProyecto("")}
            className={`w-full text-left text-sm px-2 py-1.5 rounded-sm mb-1 ${!filtroProyecto ? "bg-primary/10 text-primary" : "hover:bg-muted"}`}
          >
            Todos
          </button>
          {proyectos.map((p) => (
            <div key={p.id} className={`group flex items-center gap-1 rounded-sm ${filtroProyecto === p.id ? "bg-primary/10" : "hover:bg-muted"}`}>
              <button
                type="button"
                onClick={() => setFiltroProyecto(p.id)}
                className="flex-1 text-left text-sm px-2 py-1.5 min-w-0"
              >
                <span className="inline-block h-2 w-2 rounded-full mr-2 align-middle" style={{ backgroundColor: p.color }} />
                <span className="truncate">{p.nombre}</span>
                {p.tareas_abiertas > 0 && (
                  <span className="ml-1 text-[10px] text-muted-foreground">({p.tareas_abiertas})</span>
                )}
              </button>
              {writable && (
                <>
                  <button
                    type="button"
                    className="p-1 text-muted-foreground hover:text-foreground"
                    title="Editar proyecto"
                    onClick={() => { setEditingProyecto(p); setFormProyecto({ nombre: p.nombre, descripcion: p.descripcion || "", color: p.color }); setOpenProyecto(true); }}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    className="p-1 text-destructive hover:bg-destructive/10"
                    title="Eliminar proyecto"
                    onClick={() => eliminarProyecto(p)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          ))}
          {proyectos.length === 0 && (
            <p className="text-xs text-muted-foreground p-2">Crea un proyecto para empezar a asignar tareas.</p>
          )}
        </aside>

        <section className="bg-card border border-border rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button type="button" onClick={prevMonth} className="p-1 hover:bg-muted rounded-sm"><ChevronLeft className="h-4 w-4" /></button>
              <div className="font-display text-lg min-w-[180px] text-center">{MESES[month]} {year}</div>
              <button type="button" onClick={nextMonth} className="p-1 hover:bg-muted rounded-sm"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <Select value={filtroEstado || "__all__"} onValueChange={(v) => setFiltroEstado(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los estados</SelectItem>
                {ESTADOS.map((e) => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-7 gap-px bg-border rounded-sm overflow-hidden">
            {DIAS.map((d) => (
              <div key={d} className="bg-muted text-[10px] uppercase tracking-wider text-center py-1.5 text-muted-foreground">{d}</div>
            ))}
            {cells.map((d, idx) => {
              if (!d) return <div key={`e-${idx}`} className="bg-card min-h-[88px]" />;
              const key = ymd(d);
              const items = byDay[key] || [];
              const isSel = key === selectedDay;
              const isToday = key === todayISO();
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedDay(key)}
                  onDoubleClick={() => writable && proyectos.length && openNewTarea(key)}
                  className={`bg-card min-h-[88px] p-1 text-left align-top ${isSel ? "ring-2 ring-inset ring-primary" : ""} ${isToday ? "bg-primary/5" : ""}`}
                >
                  <div className={`text-[11px] mb-1 ${isToday ? "font-semibold text-primary" : "text-muted-foreground"}`}>{d.getDate()}</div>
                  <div className="space-y-0.5">
                    {items.slice(0, 3).map((t) => (
                      <div key={t.id} className="truncate text-[10px] px-1 rounded-sm" style={{ backgroundColor: `${proyectoMap[t.proyecto_id]?.color || "#1B4332"}22`, color: proyectoMap[t.proyecto_id]?.color }}>
                        {t.hora} {t.titulo}
                      </div>
                    ))}
                    {items.length > 3 && <div className="text-[10px] text-muted-foreground">+{items.length - 3}</div>}
                  </div>
                </button>
              );
            })}
          </div>
          {loading && <div className="text-center text-sm text-muted-foreground mt-3">Cargando…</div>}
        </section>

        <aside className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <div className="label-caps">Agenda del día</div>
          </div>
          <div className="text-sm font-medium mb-3">{selectedDay.split("-").reverse().join("/")}</div>
          {dayTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin tareas este día.</p>
          ) : (
            <ul className="space-y-2">
              {dayTasks.map((t) => {
                const p = proyectoMap[t.proyecto_id];
                return (
                  <li key={t.id} className="border border-border rounded-sm p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[11px] font-mono text-muted-foreground">{t.hora || "—"}</div>
                        <div className="text-sm font-medium">{t.titulo}</div>
                        <div className="text-[11px] truncate" style={{ color: p?.color }}>{p?.nombre}</div>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${estadoClass(t.estado)}`}>
                        {ESTADOS.find((e) => e.id === t.estado)?.label}
                      </span>
                    </div>
                    {t.notas && <p className="text-[11px] text-muted-foreground mt-1">{t.notas}</p>}
                    {writable && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ESTADOS.filter((e) => e.id !== t.estado).map((e) => (
                          <button key={e.id} type="button" onClick={() => setEstado(t, e.id)}
                            className="text-[10px] px-1.5 py-0.5 border border-border rounded-sm hover:bg-muted">
                            {e.label}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => abrirReagendar(t)}
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-primary text-primary rounded-sm hover:bg-primary/10"
                        >
                          <CalendarClock className="h-3 w-3" /> Reagendar
                        </button>
                        <button type="button" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-border rounded-sm hover:bg-muted"
                          onClick={() => { setEditingTarea(t); setFormTarea({ proyecto_id: t.proyecto_id, titulo: t.titulo, notas: t.notas || "", fecha: t.fecha, hora: t.hora || "09:00", recordatorio_minutos: t.recordatorio_minutos ?? 15, estado: t.estado }); setOpenTarea(true); }}>
                          <Pencil className="h-3 w-3" /> Editar
                        </button>
                        <button type="button" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-destructive text-destructive rounded-sm hover:bg-destructive/10"
                          onClick={() => eliminarTarea(t)}>
                          <Trash2 className="h-3 w-3" /> Eliminar
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      <Dialog open={openProyecto} onOpenChange={setOpenProyecto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProyecto ? "Editar proyecto" : "Nuevo proyecto"}</DialogTitle>
            <DialogDescription>Asigna un nombre y color para agrupar las tareas.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveProyecto} className="space-y-3">
            <div>
              <label className="label-caps block mb-1">Nombre</label>
              <input required value={formProyecto.nombre} onChange={(e) => setFormProyecto({ ...formProyecto, nombre: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="label-caps block mb-1">Descripción</label>
              <input value={formProyecto.descripcion} onChange={(e) => setFormProyecto({ ...formProyecto, descripcion: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="label-caps block mb-1">Color</label>
              <div className="flex gap-2">
                {COLORES.map((c) => (
                  <button key={c} type="button" onClick={() => setFormProyecto({ ...formProyecto, color: c })}
                    className={`h-6 w-6 rounded-full ${formProyecto.color === c ? "ring-2 ring-offset-2 ring-primary" : ""}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <DialogFooter>
              {editingProyecto && (
                <div className="mr-auto flex gap-2">
                  <button type="button" className="text-sm text-muted-foreground hover:underline"
                    onClick={async () => {
                      if (!window.confirm("¿Archivar este proyecto? Dejará de verse en la lista, pero no se borra.")) return;
                      try {
                        await api.post(`/organizador/proyectos/${editingProyecto.id}/archivar`);
                        toast.success("Proyecto archivado");
                        setOpenProyecto(false);
                        setFiltroProyecto("");
                        loadProyectos();
                      } catch (err) { toast.error(extractError(err)); }
                    }}>Archivar</button>
                  <button type="button" className="text-sm text-destructive hover:underline"
                    onClick={() => eliminarProyecto(editingProyecto)}>Eliminar</button>
                </div>
              )}
              <button type="submit" className="bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">Guardar</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openTarea} onOpenChange={setOpenTarea}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTarea ? "Editar tarea" : "Nueva tarea"}</DialogTitle>
            <DialogDescription>Programa día, hora y un recordatorio antes del vencimiento.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveTarea} className="space-y-3">
            <div>
              <label className="label-caps block mb-1">Proyecto</label>
              <Select value={formTarea.proyecto_id} onValueChange={(v) => setFormTarea({ ...formTarea, proyecto_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                <SelectContent>
                  {proyectos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-caps block mb-1">Título</label>
              <input required value={formTarea.titulo} onChange={(e) => setFormTarea({ ...formTarea, titulo: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Fecha</label>
                <input type="date" required value={formTarea.fecha} onChange={(e) => setFormTarea({ ...formTarea, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-caps block mb-1">Hora</label>
                <input type="time" required value={formTarea.hora} onChange={(e) => setFormTarea({ ...formTarea, hora: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Recordatorio</label>
                <Select value={String(formTarea.recordatorio_minutos)} onValueChange={(v) => setFormTarea({ ...formTarea, recordatorio_minutos: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECORDATORIOS.map((r) => <SelectItem key={r.v} value={String(r.v)}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">Estado</label>
                <Select value={formTarea.estado} onValueChange={(v) => setFormTarea({ ...formTarea, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((e) => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1">Notas</label>
              <textarea value={formTarea.notas} onChange={(e) => setFormTarea({ ...formTarea, notas: e.target.value })}
                rows={3} className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <DialogFooter>
              {editingTarea && (
                <button type="button" className="mr-auto text-sm text-destructive hover:underline"
                  onClick={() => { setOpenTarea(false); eliminarTarea(editingTarea); }}>
                  Eliminar tarea
                </button>
              )}
              <button type="submit" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                <Bell className="h-4 w-4" /> Guardar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openReagendar} onOpenChange={setOpenReagendar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reagendar / mover tarea</DialogTitle>
            <DialogDescription>
              Elige el nuevo día y hora. La tarea se asignará sola a esa fecha en el calendario.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmarReagendar} className="space-y-3">
            {tareaReagendar && (
              <div className="text-sm bg-muted rounded-sm px-3 py-2">
                <div className="font-medium">{tareaReagendar.titulo}</div>
                <div className="text-xs text-muted-foreground">Hoy está en {tareaReagendar.fecha.split("-").reverse().join("/")} {tareaReagendar.hora || ""}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Nueva fecha</label>
                <input type="date" required value={formReagendar.fecha}
                  onChange={(e) => setFormReagendar({ ...formReagendar, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
              <div>
                <label className="label-caps block mb-1">Nueva hora</label>
                <input type="time" required value={formReagendar.hora}
                  onChange={(e) => setFormReagendar({ ...formReagendar, hora: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpenReagendar(false)} className="border border-border text-sm px-4 py-2 rounded-sm">Cancelar</button>
              <button type="submit" className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm px-4 py-2 rounded-sm">
                <CalendarClock className="h-4 w-4" /> Mover a esa fecha
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
